import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';
import BookIcon from '@mui/icons-material/Book';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  ProtocolTemplatesTab,
  FollowUpSchedulesTab,
  EscalationStrategiesTab,
} from '../../components/clinician/adherence/AdherenceKnowledgeTabs';
import {
  loadFoodClassifications,
  loadProtocolTemplatesWithDetails,
  loadFollowUpSchedules,
  loadEscalationStrategiesWithActions,
  type FoodClassification,
  type ProtocolTemplateWithDetails,
  type FollowUpScheduleRecord,
  type EscalationStrategyWithActions,
} from '../../utils/loadDataFromContract';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`knowledge-tabpanel-${index}`} aria-labelledby={`knowledge-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdherencePage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [foodClassifications, setFoodClassifications] = useState<FoodClassification[]>([]);
  const [protocolTemplates, setProtocolTemplates] = useState<ProtocolTemplateWithDetails[]>([]);
  const [followUpSchedules, setFollowUpSchedules] = useState<FollowUpScheduleRecord[]>([]);
  const [escalationStrategies, setEscalationStrategies] = useState<EscalationStrategyWithActions[]>([]);
  const [openFoodDialog, setOpenFoodDialog] = useState(false);
  const [editingFood, setEditingFood] = useState<FoodClassification | null>(null);

  const [foodCategory, setFoodCategory] = useState('');
  const [foodName, setFoodName] = useState('');
  const [foodClassification, setFoodClassification] = useState<FoodClassification['classification']>('GREEN');
  const [foodPortion, setFoodPortion] = useState('');
  const [foodNotes, setFoodNotes] = useState('');

  useEffect(() => {
    setFoodClassifications(loadFoodClassifications());
    setProtocolTemplates(loadProtocolTemplatesWithDetails());
    setFollowUpSchedules(loadFollowUpSchedules());
    setEscalationStrategies(loadEscalationStrategiesWithActions());
  }, []);

  const handleOpenFoodDialog = (food?: FoodClassification) => {
    if (food) {
      setEditingFood(food);
      setFoodCategory(food.category);
      setFoodName(food.food);
      setFoodClassification(food.classification);
      setFoodPortion(food.portion);
      setFoodNotes(food.notes);
    } else {
      setEditingFood(null);
      setFoodCategory('');
      setFoodName('');
      setFoodClassification('GREEN');
      setFoodPortion('');
      setFoodNotes('');
    }
    setOpenFoodDialog(true);
  };

  const handleCloseFoodDialog = () => {
    setOpenFoodDialog(false);
    setEditingFood(null);
  };

  const handleSaveFood = () => {
    const newFood: FoodClassification = {
      id: editingFood ? editingFood.id : `food-${Date.now()}`,
      category: foodCategory,
      food: foodName,
      classification: foodClassification,
      portion: foodPortion,
      notes: foodNotes,
    };

    if (editingFood) {
      setFoodClassifications(foodClassifications.map((f) => (f.id === editingFood.id ? newFood : f)));
    } else {
      setFoodClassifications([...foodClassifications, newFood]);
    }

    handleCloseFoodDialog();
  };

  const handleDeleteFood = (id: string) => {
    if (window.confirm('Are you sure you want to delete this food classification?')) {
      setFoodClassifications(foodClassifications.filter((f) => f.id !== id));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">Knowledge Base</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Standard food classifications, protocol templates, follow-up schedules, and escalation strategies to inform patient-specific protocols
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={(_event, newValue) => setTabValue(newValue)} aria-label="knowledge base tabs">
          <Tab icon={<RestaurantIcon />} iconPosition="start" label="Food Classifications" />
          <Tab icon={<BookIcon />} iconPosition="start" label="Protocol Templates" />
          <Tab icon={<ScheduleIcon />} iconPosition="start" label="Follow-Up Schedules" />
          <Tab icon={<WarningIcon />} iconPosition="start" label="Escalation Strategies" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
              Standard food classifications based on glycemic impact and evidence-based recommendations.
              Use these as a starting point and personalize based on individual CGM data.
            </Alert>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenFoodDialog()}>
              Add Food
            </Button>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Classification System
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderLeft: 4, borderColor: 'success.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'success.main' }} />
                      GREEN Foods
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Eat freely. Minimal glycemic impact. High fiber, nutrients. Core of meal plan.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'warning.main' }} />
                      YELLOW Foods
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Eat in moderation. Watch portions. Pair with protein/fat. Limit to 1 per meal.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'error.main' }} />
                      RED Foods
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avoid or strictly limit. Causes rapid spikes. Low nutrients. Special occasions only.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Food Item</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell>Portion Guide</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {foodClassifications.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{item.food}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.classification}
                        size="small"
                        sx={{
                          bgcolor:
                            item.classification === 'GREEN'
                              ? 'success.main'
                              : item.classification === 'YELLOW'
                              ? 'warning.main'
                              : 'error.main',
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell>{item.portion}</TableCell>
                    <TableCell>{item.notes}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenFoodDialog(item)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteFood(item.id)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <ProtocolTemplatesTab protocolTemplates={protocolTemplates} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <FollowUpSchedulesTab followUpSchedules={followUpSchedules} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <EscalationStrategiesTab escalationStrategies={escalationStrategies} />
        </TabPanel>
      </Paper>

      <Dialog open={openFoodDialog} onClose={handleCloseFoodDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFood ? 'Edit Food Classification' : 'Add New Food Classification'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={foodCategory} label="Category" onChange={(e) => setFoodCategory(e.target.value)}>
                <MenuItem value="Grains & Starches">Grains & Starches</MenuItem>
                <MenuItem value="Vegetables">Vegetables</MenuItem>
                <MenuItem value="Fruits">Fruits</MenuItem>
                <MenuItem value="Proteins">Proteins</MenuItem>
                <MenuItem value="Dairy">Dairy</MenuItem>
                <MenuItem value="Nuts & Seeds">Nuts & Seeds</MenuItem>
                <MenuItem value="Snacks & Sweets">Snacks & Sweets</MenuItem>
                <MenuItem value="Beverages">Beverages</MenuItem>
                <MenuItem value="Fats & Oils">Fats & Oils</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Food Name"
              value={foodName}
              onChange={(e) => setFoodName(e.target.value)}
              placeholder="e.g., Brown rice"
            />

            <FormControl fullWidth>
              <InputLabel>Classification</InputLabel>
              <Select
                value={foodClassification}
                label="Classification"
                onChange={(e) => setFoodClassification(e.target.value as 'GREEN' | 'YELLOW' | 'RED')}
              >
                <MenuItem value="GREEN">GREEN - Eat Freely</MenuItem>
                <MenuItem value="YELLOW">YELLOW - Moderate</MenuItem>
                <MenuItem value="RED">RED - Avoid/Limit</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Portion Guide"
              value={foodPortion}
              onChange={(e) => setFoodPortion(e.target.value)}
              placeholder="e.g., 1 cup, 1 slice, 3 oz"
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={foodNotes}
              onChange={(e) => setFoodNotes(e.target.value)}
              placeholder="Add clinical notes about glycemic impact, recommendations, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFoodDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFood} disabled={!foodCategory || !foodName || !foodPortion}>
            {editingFood ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
